import { buildAbsoluteCanonicalUrl } from './canonical-url.util';
import { buildListingPublicPath } from './listing-route.util';
import {
  buildPropertyStatsSegments,
  formatPropertyLocationLine,
  formatPropertyPrice,
  propertyImagePaths,
  propertyTypeFrom,
} from './property-card.util';
import { resolveListingStatusBadge } from './listing-status.util';

export const LISTING_SEO_SITE_NAME = 'Gabali Realty';
export const LISTING_SEO_DEFAULT_TITLE = LISTING_SEO_SITE_NAME;

export interface ListingSeoPayload {
  title: string;
  description: string;
  canonicalUrl: string;
  imageUrl: string | null;
  jsonLd: Record<string, unknown>;
}

export function buildListingCanonicalUrl(
  property: Record<string, unknown>,
  siteOrigin: string,
): string {
  return buildAbsoluteCanonicalUrl(siteOrigin, buildListingPublicPath(property));
}

export function buildListingSeo(
  property: Record<string, unknown>,
  siteOrigin: string,
): ListingSeoPayload {
  const addressLine = formatPropertyLocationLine(property);
  const priceLabel = formatPropertyPrice(property['Price']);
  const title = buildListingTitle(addressLine, priceLabel);
  const description = buildListingDescription(property);
  const canonicalUrl = buildListingCanonicalUrl(property, siteOrigin);
  const imageUrl = resolveListingImageUrl(property, siteOrigin);

  return {
    title,
    description,
    canonicalUrl,
    imageUrl,
    jsonLd: buildListingJsonLd(property, {
      title,
      description,
      canonicalUrl,
      imageUrl,
    }),
  };
}

function buildListingTitle(addressLine: string, priceLabel: string): string {
  const address = addressLine.trim() || 'Property listing';
  const price = priceLabel.trim();

  if (price) {
    return `${address} | ${price} | ${LISTING_SEO_SITE_NAME}`;
  }

  return `${address} | ${LISTING_SEO_SITE_NAME}`;
}

function buildListingDescription(property: Record<string, unknown>): string {
  const propertyType = propertyTypeFrom(property);
  const stats = buildPropertyStatsSegments(property, propertyType)
    .map((segment) => `${segment.value} ${segment.label}`.trim())
    .filter(Boolean)
    .join(' · ');
  const priceLabel = formatPropertyPrice(property['Price']);
  const addressLine = formatPropertyLocationLine(property);
  const remarks = String(property['Remarks'] ?? '').replace(/\s+/g, ' ').trim();

  const leadParts = [stats, priceLabel, addressLine].filter(Boolean);
  const lead = leadParts.join(' · ');

  if (!remarks) {
    return truncateText(lead, 160);
  }

  const separator = lead ? '. ' : '';
  const availableForRemarks = 160 - lead.length - separator.length;

  if (availableForRemarks <= 0) {
    return truncateText(lead, 160);
  }

  return truncateText(`${lead}${separator}${remarks}`, 160);
}

function buildListingJsonLd(
  property: Record<string, unknown>,
  context: {
    title: string;
    description: string;
    canonicalUrl: string;
    imageUrl: string | null;
  },
): Record<string, unknown> {
  const address = property['Address'] as Record<string, unknown> | undefined;
  const propertyType = propertyTypeFrom(property);
  const price = Number(property['Price']);
  const badge = resolveListingStatusBadge(property);
  const images = propertyImagePaths(property)
    .map((path) => toAbsoluteUrl(path, context.canonicalUrl))
    .filter(Boolean);

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: context.title,
    description: context.description,
    url: context.canonicalUrl,
    image: images.length > 0 ? images : context.imageUrl ? [context.imageUrl] : undefined,
    offers: Number.isFinite(price)
      ? {
          '@type': 'Offer',
          price,
          priceCurrency: 'USD',
          availability: mapListingAvailability(badge?.label),
          url: context.canonicalUrl,
        }
      : undefined,
    address: {
      '@type': 'PostalAddress',
      streetAddress: formatStreetAddress(property),
      addressLocality: String(address?.['City'] ?? '').trim() || undefined,
      addressRegion: String(address?.['State'] ?? '').trim() || undefined,
      postalCode: String(address?.['Zip'] ?? address?.['ZIP'] ?? '').trim() || undefined,
      addressCountry: 'US',
    },
  };

  const geo = buildGeoCoordinates(property);
  if (geo) {
    jsonLd['geo'] = geo;
  }

  if (propertyType === 'residential') {
    const bedrooms = Number(property['Bedrooms']);
    const bathrooms = Number(property['FullBaths']);
    const area = parseNumericArea(property['Area']);

    if (Number.isFinite(bedrooms)) {
      jsonLd['numberOfBedrooms'] = bedrooms;
    }

    if (Number.isFinite(bathrooms)) {
      jsonLd['numberOfBathroomsTotal'] = bathrooms;
    }

    if (Number.isFinite(area)) {
      jsonLd['floorSize'] = {
        '@type': 'QuantitativeValue',
        value: area,
        unitCode: 'FTK',
      };
    }
  }

  return pruneUndefined(jsonLd);
}

function buildGeoCoordinates(property: Record<string, unknown>): Record<string, unknown> | null {
  const coordinates = property['Coordinates'];
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return null;
  }

  const lng = Number(coordinates[0]);
  const lat = Number(coordinates[1]);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return {
    '@type': 'GeoCoordinates',
    latitude: lat,
    longitude: lng,
  };
}

function formatStreetAddress(property: Record<string, unknown>): string | undefined {
  const address = property['Address'] as Record<string, unknown> | undefined;
  if (!address) {
    return undefined;
  }

  const street = `${address['Number'] ?? ''} ${address['Street'] ?? ''}`.trim();
  return street || undefined;
}

function mapListingAvailability(statusLabel: string | undefined): string | undefined {
  if (!statusLabel) {
    return 'https://schema.org/InStock';
  }

  const normalized = statusLabel.toLowerCase();

  if (normalized.includes('sold')) {
    return 'https://schema.org/SoldOut';
  }

  if (normalized.includes('pending') || normalized.includes('contingent')) {
    return 'https://schema.org/LimitedAvailability';
  }

  return 'https://schema.org/InStock';
}

function resolveListingImageUrl(
  property: Record<string, unknown>,
  siteOrigin: string,
): string | null {
  const firstImage = propertyImagePaths(property)[0];
  if (!firstImage) {
    return null;
  }

  return toAbsoluteUrl(firstImage, siteOrigin);
}

function toAbsoluteUrl(url: string, siteOrigin: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const origin = siteOrigin.replace(/\/$/, '');
  if (!origin) {
    return trimmed;
  }

  return `${origin}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
}

function parseNumericArea(value: unknown): number | null {
  const normalized = String(value ?? '').replace(/,/g, '').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function truncateText(value: string, maxLength: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }

  return `${trimmed.slice(0, maxLength - 1).trimEnd()}…`;
}

function pruneUndefined(value: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, entry] of Object.entries(value)) {
    if (entry === undefined) {
      continue;
    }

    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const nested = pruneUndefined(entry as Record<string, unknown>);
      if (Object.keys(nested).length > 0) {
        result[key] = nested;
      }
      continue;
    }

    if (Array.isArray(entry)) {
      const filtered = entry.filter((item) => item !== undefined);
      if (filtered.length > 0) {
        result[key] = filtered;
      }
      continue;
    }

    result[key] = entry;
  }

  return result;
}
